import { dehydrate, QueryClient } from '@tanstack/react-query';
import { Body, Heading, IconCaret, IconExternalLink, Stack, TextButton, Tile, Wrapper } from 'designsystem';
import { GetServerSideProps } from 'next';
import { FormattedMessage, useIntl } from 'react-intl';
import { gtm, SearchTemplate } from 'shared';
import loadIntlMessages from '../utils/loadIntlMessages';
import { useEffect } from 'react';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';

const Search = () => {
    const { locale } = useRouter();
    const { formatMessage } = useIntl();

    useEffect(() => {
        gtm.pageView('/search', {
            page_pillar: 'festival',
            page_navigation: 'search',
            page_language: locale,
        });
    }, [locale]);

    return (
        <main>
            <NextSeo title={formatMessage({ defaultMessage: 'Zoeken' })} />
            <Wrapper my={8}>
                <Heading variant={1} mb={7}>
                    <FormattedMessage defaultMessage="Zoeken" />
                </Heading>

                <SearchTemplate
                    tiles={[
                        <Tile
                            key="popular-searches-tile"
                            size="s"
                            // sx={{ aspectRatio: '1' }}
                            colorScheme="neutral"
                            w="100%"
                            maxW="368px"
                        >
                            <Stack spacing={8}>
                                <Heading variant={3}>
                                    <FormattedMessage id="most-searched-title" defaultMessage="Meest gezochte termen" />
                                </Heading>
                                <Stack spacing={2}>
                                    <Heading variant={4}>Festival</Heading>
                                    <TextButton
                                        href={
                                            locale === 'en'
                                                ? `${process.env.NEXT_PUBLIC_PILLAR_URL_FESTIVAL}en/`
                                                : `${process.env.NEXT_PUBLIC_PILLAR_URL_FESTIVAL}`
                                        }
                                        size="s"
                                        iconRight={<IconCaret />}
                                    >
                                        <FormattedMessage defaultMessage="Programma" />
                                    </TextButton>
                                </Stack>
                                <Stack spacing={2}>
                                    <Heading variant={4}>Professionals</Heading>
                                    <TextButton
                                        href={`${process.env.NEXT_PUBLIC_PILLAR_URL_PROFESSIONALS}program/industry-program/`}
                                        size="s"
                                        iconRight={<IconExternalLink />}
                                    >
                                        Industry Program
                                    </TextButton>
                                    <TextButton
                                        href={`${process.env.NEXT_PUBLIC_PILLAR_URL_PROFESSIONALS}markets/entries/`}
                                        size="s"
                                        iconRight={<IconExternalLink />}
                                    >
                                        Call for Entry
                                    </TextButton>
                                </Stack>
                                <Stack spacing={2}>
                                    <Heading variant={4}>Institute</Heading>
                                    <TextButton
                                        href={
                                            locale === 'en'
                                                ? `${process.env.NEXT_PUBLIC_PILLAR_URL_INSTITUTE}en/vondelpark/`
                                                : `${process.env.NEXT_PUBLIC_PILLAR_URL_INSTITUTE}vondelpark/`
                                        }
                                        size="s"
                                        iconRight={<IconExternalLink />}
                                    >
                                        Het Documentaire Paviljoen
                                    </TextButton>
                                    <TextButton
                                        href={
                                            locale === 'en'
                                                ? `${process.env.NEXT_PUBLIC_PILLAR_URL_INSTITUTE}en/about-idfa/organisation/about-idfa/annual-reports/`
                                                : `${process.env.NEXT_PUBLIC_PILLAR_URL_INSTITUTE}over-idfa/organisation/about-idfa/jaarverslagen/`
                                        }
                                        size="s"
                                        iconRight={<IconExternalLink />}
                                    >
                                        <FormattedMessage
                                            defaultMessage="Jaarverslagen"
                                            id="most-searched-annual-reports"
                                        />
                                    </TextButton>
                                    <TextButton
                                        href={
                                            locale === 'en'
                                                ? `${process.env.NEXT_PUBLIC_PILLAR_URL_INSTITUTE}en/about-idfa/organisation/werken-bij-idfa/`
                                                : `${process.env.NEXT_PUBLIC_PILLAR_URL_INSTITUTE}over-idfa/organisation/werken-bij-idfa/`
                                        }
                                        size="s"
                                        iconRight={<IconExternalLink />}
                                    >
                                        <FormattedMessage defaultMessage="Vacatures" id="most-searched-vacancies" />
                                    </TextButton>
                                </Stack>
                            </Stack>
                        </Tile>,
                        <Tile
                            key="archive-tile"
                            size="s"
                            sx={{ aspectRatio: '1' }}
                            colorScheme="institute"
                            w="100%"
                            maxW="368px"
                        >
                            <Stack spacing={6}>
                                <Stack spacing={4}>
                                    <Heading variant={3}>
                                        <FormattedMessage
                                            defaultMessage="Ontdek het IDFA archief"
                                            id="archive-cta-title"
                                        />
                                    </Heading>
                                    <Body>
                                        <FormattedMessage
                                            defaultMessage="Bekijk alle films, interactieve projecten, prijswinnaars, filmmakers en talks van de vorige IDFA edities."
                                            id="archive-cta-desc"
                                        />
                                    </Body>
                                </Stack>
                                <TextButton
                                    href={
                                        locale === 'en'
                                            ? `${process.env.NEXT_PUBLIC_PILLAR_URL_INSTITUTE}en/about-idfa/archive/`
                                            : `${process.env.NEXT_PUBLIC_PILLAR_URL_INSTITUTE}over-idfa/archive/`
                                    }
                                    size="s"
                                    iconRight={<IconExternalLink />}
                                >
                                    <FormattedMessage defaultMessage="IDFA archief" id="archive-cta-link" />
                                </TextButton>
                            </Stack>
                        </Tile>,
                    ]}
                />
            </Wrapper>
        </main>
    );
};

export const getServerSideProps: GetServerSideProps = async ({ locale }) => {
    const queryClient = new QueryClient();

    return {
        props: {
            dehydratedState: dehydrate(queryClient),
            intlMessages: await loadIntlMessages(locale),
        },
    };
};

export default Search;
